import { Pencil, Plus } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";
import type {
	IncentiveRule,
	PackageOptionGroup,
} from "@/types/admin-portal/bonus-ro";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import { LoadFailed, Loading } from "./load-state";
import { AddRuleDialog, ChangeAmountDialog } from "./rule-dialogs";
import { addablePackages, formatDay, groupRules } from "./rules";
import { TabNote } from "./tab-note";

export interface RulesTabProps {
	rules?: ExternalProp<IncentiveRule[]>;
	packageOptions: PackageOptionGroup[];
	minStartsOn: string;
	routes: { rules: string; ruleAmount: string };
}

export function RulesTab({
	rules,
	packageOptions,
	minStartsOn,
	routes,
}: RulesTabProps) {
	const { t } = useTranslation("bonus-ro");
	const [editing, setEditing] = useState<IncentiveRule | null>(null);
	const [adding, setAdding] = useState(false);

	const list = rules?.data ?? [];
	const groups = groupRules(list);
	const addable = addablePackages(packageOptions, list, minStartsOn);

	if (!rules) return <Loading />;
	if (rules.error) {
		return (
			<LoadFailed title={t("failed.rules")} error={rules.error} only="rules" />
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<TabNote>{t("rules.description")}</TabNote>
				<div className="flex shrink-0 flex-col gap-1 sm:items-end">
					<Button
						className="self-start sm:self-end"
						disabled={addable.length === 0}
						onClick={() => setAdding(true)}
					>
						<Plus />
						{t("rules.add")}
					</Button>
					{addable.length === 0 && (
						<p className="text-xs text-muted-foreground">
							{t("rules.add_none")}
						</p>
					)}
				</div>
			</div>

			{groups.length === 0 ? (
				<div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
					{t("rules.empty")}
				</div>
			) : (
				groups.map((service) => (
					<section key={service.serviceName} className="flex flex-col gap-2">
						<h2 className="text-sm font-semibold tracking-tight">
							{service.serviceName.replaceAll("_", " ")}
						</h2>
						<div className="overflow-x-auto rounded-lg border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("rules.columns.package")}</TableHead>
										<TableHead className="text-right">
											{t("rules.columns.amount")}
										</TableHead>
										<TableHead className="text-right">
											{t("rules.columns.customer_discount")}
										</TableHead>
										<TableHead>{t("rules.columns.valid_from")}</TableHead>
										<TableHead>{t("rules.columns.valid_until")}</TableHead>
										<TableHead>{t("rules.columns.status")}</TableHead>
										<TableHead className="w-10" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{service.packages.map((pkg) => (
										<Fragment key={pkg.packageId}>
											{pkg.rules.map((rule, index) => (
												<TableRow key={rule.id}>
													{index === 0 && (
														<TableCell
															rowSpan={pkg.rules.length}
															className="align-top font-medium"
														>
															{pkg.packageName}
															{pkg.numberOfVisit !== null && (
																<span className="block text-xs font-normal text-muted-foreground">
																	{t("rules.visits", {
																		count: pkg.numberOfVisit,
																	})}
																</span>
															)}
														</TableCell>
													)}
													<TableCell className="text-right tabular-nums">
														{formatCurrency(rule.amount)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{rule.customerDiscountAmount === null
															? "—"
															: formatCurrency(rule.customerDiscountAmount)}
													</TableCell>
													<TableCell className="text-nowrap">
														{formatDay(rule.validFrom)}
													</TableCell>
													<TableCell className="text-nowrap">
														{rule.validUntil
															? formatDay(rule.validUntil)
															: t("rules.open_ended")}
													</TableCell>
													<TableCell>
														{rule.active && (
															<Badge variant="success">
																{t("rules.active")}
															</Badge>
														)}
													</TableCell>
													<TableCell className="text-right">
														{pkg.openRule?.id === rule.id && (
															<Button
																variant="ghost"
																size="sm"
																className="text-muted-foreground hover:text-foreground"
																onClick={() => setEditing(rule)}
															>
																<Pencil />
																{t("rules.change_amount")}
															</Button>
														)}
													</TableCell>
												</TableRow>
											))}
										</Fragment>
									))}
								</TableBody>
							</Table>
						</div>
					</section>
				))
			)}

			{editing && (
				<ChangeAmountDialog
					rule={editing}
					amountUrl={routes.ruleAmount}
					createUrl={routes.rules}
					today={minStartsOn}
					onClose={() => setEditing(null)}
				/>
			)}
			{adding && (
				<AddRuleDialog
					options={addable}
					createUrl={routes.rules}
					today={minStartsOn}
					onClose={() => setAdding(false)}
				/>
			)}
		</div>
	);
}
